import { useId, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Printer, Mail, Download } from 'lucide-react';
import { useApp } from '@/store';
import { Lang, useT } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fmtCurrency, fmtDate } from '@/lib/utils';
import { applyRounding, getCountry } from '@/lib/country';
import { exportPdf } from '@/lib/pdf';
import { ResponsiveGrid, ScrollTable } from '@/components/layout';

export function Invoice() {
  const { id } = useParams();
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const settings = useApp(s => s.settings);
  const servicePrices = useApp(s => s.servicePrices);
  const spareParts = useApp(s => s.spareParts);
  const types = useApp(s => s.deviceTypes);
  const lang = useApp(s => s.lang);
  const t = useT();
  const printRef = useRef<HTMLDivElement>(null);
  const vatInputId = useId();
  const [priceOverrides, setPriceOverrides] = useState<Record<string, number>>(
    {}
  );
  const [vatOverride, setVatOverride] = useState<number | null>(null);
  const setLinePrice = (key: string, price: number) =>
    setPriceOverrides(prev => ({ ...prev, [key]: price }));

  const handlePdf = () => {
    if (printRef.current) {
      exportPdf(
        printRef.current,
        `RE-${customer?.id}-${new Date().getFullYear()}.pdf`
      );
    }
  };

  const lines = useMemo(() => {
    if (!customer) return [];
    const out: {
      section: string;
      items: { articleNr: string; desc: string; qty: number; price: number }[];
    }[] = [];
    const grouped: Record<string, typeof customer.extinguishers> = {};
    customer.extinguishers.forEach(e => {
      const k = e.type ?? 'misc';
      grouped[k] = grouped[k] ?? [];
      grouped[k].push(e);
    });
    Object.entries(grouped).forEach(([type, items]) => {
      const tRow = types.find(x => x.id === type);
      const tName = tRow
        ? ((lang === Lang.DE ? tRow.nameDe : tRow.nameEn) ?? tRow.nameDe)
        : type;
      const priceRow = servicePrices.find(p => p.deviceType === type);
      const price = priceRow?.price ?? 0;
      const serviceLine = {
        articleNr: priceRow?.articleNr ?? '—',
        desc: `${t('invoice.maintenance')} ${tName}`,
        qty: items.length,
        price,
      };
      const agg: Record<string, { desc: string; qty: number; price: number }> =
        {};
      items.forEach(e => {
        (e.spareParts ?? []).forEach(a => {
          const part = spareParts.find(p => p.articleNr === a);
          if (!part) return;
          if (!agg[part.articleNr])
            agg[part.articleNr] = {
              desc: part.name,
              qty: 0,
              price: part.price,
            };
          agg[part.articleNr].qty += 1;
        });
      });
      const partLines = Object.entries(agg).map(([articleNr, v]) => ({
        articleNr,
        desc: v.desc,
        qty: v.qty,
        price: v.price,
      }));
      if (customer.hoses.length && type === 'loeschposten') {
        // already counted via extinguishers? no — hoses are separate; skip here
      }
      out.push({ section: tName, items: [serviceLine, ...partLines] });
    });
    // Hose maintenance section
    if (customer.hoses.length) {
      const priceRow = servicePrices.find(p => p.deviceType === 'loeschposten');
      const price = priceRow?.price ?? 0;
      out.push({
        section: t('invoice.fireHoses'),
        items: [
          {
            articleNr: priceRow?.articleNr ?? '—',
            desc: t('invoice.hoseMaintenance'),
            qty: customer.hoses.length,
            price,
          },
        ],
      });
    }
    return out;
  }, [customer, servicePrices, spareParts, types, lang]);

  if (!customer) return <div>{t('common.notFound')}</div>;

  const country = getCountry(settings.country);

  const linesTotal = lines
    .flatMap((l, si) =>
      l.items.map((i, ii) => (priceOverrides[`${si}-${ii}`] ?? i.price) * i.qty)
    )
    .reduce((s, v) => s + v, 0);
  const subtotal = linesTotal + settings.travelSurcharge;
  const effectiveVat = vatOverride ?? settings.vatPercent;
  const vat = subtotal * (effectiveVat / 100);
  const totalBeforeRound = subtotal + vat;
  const total = applyRounding(totalBeforeRound, settings.rounding);
  const roundingDiff = total - totalBeforeRound;

  const todayIso = new Date().toISOString().slice(0, 10);
  const dueIso = new Date(Date.now() + settings.paymentTerms * 86400000)
    .toISOString()
    .slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="no-print flex justify-between items-center">
        <Link
          to={`/customer/${customer.id}`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            {t('common.print')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-focus="invoice-email"
            onClick={() => {
              const email =
                customer.billingAddress?.email ?? customer.contact?.email ?? '';
              const subject = encodeURIComponent(
                `Invoice RE-${customer.id}-${new Date().getFullYear()}`
              );
              const body = encodeURIComponent(
                `${settings.companyName}\n${customer.name}\n\nTotal: ${fmtCurrency(total, settings.currency)}\nDue: ${fmtDate(dueIso)}`
              );
              window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
            }}
          >
            <Mail className="h-4 w-4" />
            {t('common.email')}
          </Button>
          <Button size="sm" onClick={handlePdf}>
            <Download className="h-4 w-4" />
            {t('common.pdf')}
          </Button>
        </div>
      </div>

      <Card
        ref={printRef}
        className="print-page max-w-[210mm] mx-auto p-4 sm:p-8"
      >
        <div className="border-b pb-4 mb-6 grid gap-4 sm:grid-cols-2">
          <div>
            <div className="font-bold text-lg text-primary">
              {settings.companyName}
            </div>
            <div className="text-xs">{settings.street}</div>
            <div className="text-xs">
              {settings.zip} {settings.city}
            </div>
            <div className="text-xs">
              {settings.phone} · {settings.email}
            </div>
            <div className="text-xs mt-1">
              {country.vatNumberLabel}: {settings.vatNumber}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold">{t('invoice.title')}</div>
            <div className="text-xs mt-2">
              #{' '}
              <span className="font-mono">
                RE-{customer.id}-{new Date().getFullYear()}
              </span>
            </div>
            <div className="text-xs">
              {t('invoice.date')}: {fmtDate(todayIso)}
            </div>
            <div className="text-xs">
              {t('invoice.due')}: {fmtDate(dueIso)}
            </div>
          </div>
        </div>

        <ResponsiveGrid cols={3} className="mb-6">
          <div className="text-sm">
            <div className="font-semibold mb-1">{t('invoice.billTo')}</div>
            <div className="text-xs leading-relaxed">
              {customer.billingAddress?.company}
              <br />
              {customer.billingAddress?.attn && (
                <>
                  {t('invoice.attn')} {customer.billingAddress.attn}
                  <br />
                </>
              )}
              {customer.billingAddress?.street}
              <br />
              {customer.billingAddress?.zip} {customer.billingAddress?.city}
            </div>
          </div>
          <div className="text-sm">
            <div className="font-semibold mb-1">{t('invoice.object')}</div>
            <div className="text-xs leading-relaxed">
              {customer.objectAddress?.company}
              <br />
              {customer.objectAddress?.street}
              <br />
              {customer.objectAddress?.zip} {customer.objectAddress?.city}
            </div>
          </div>
          <div className="text-sm">
            <div className="font-semibold mb-1">{t('invoice.owner')}</div>
            <div className="text-xs leading-relaxed">
              {customer.ownerAddress?.company ?? '—'}
              <br />
              {customer.ownerAddress?.street}
              <br />
              {customer.ownerAddress?.zip} {customer.ownerAddress?.city}
            </div>
          </div>
        </ResponsiveGrid>

        <ScrollTable minWidth={500}>
          <table className="w-full text-xs mb-4">
            <thead className="border-b">
              <tr className="text-left">
                <th className="py-2 w-10">#</th>
                <th>{t('invoice.article')}</th>
                <th>{t('invoice.description')}</th>
                <th className="text-right w-16">{t('invoice.qty')}</th>
                <th className="w-12"></th>
                <th className="text-right w-24" data-focus="invoice-price">
                  {t('invoice.price')}
                </th>
                <th className="text-right w-24">{t('invoice.total')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.flatMap((sec, si) => [
                <tr key={`s-${si}`} className="bg-primary/5">
                  <td colSpan={7} className="py-1 px-2 font-semibold">
                    {sec.section}
                  </td>
                </tr>,
                ...sec.items.map((it, ii) => {
                  const key = `${si}-${ii}`;
                  const effectivePrice = priceOverrides[key] ?? it.price;
                  return (
                    <tr
                      key={key}
                      data-testid="invoice-line-row"
                      className="border-b"
                    >
                      <td className="py-1">{ii + 1}</td>
                      <td className="font-mono">{it.articleNr}</td>
                      <td>{it.desc}</td>
                      <td className="text-right">{it.qty}</td>
                      <td className="text-muted-foreground">
                        {t('invoice.unit')}
                      </td>
                      <td className="text-right no-print">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={effectivePrice}
                          onChange={e =>
                            setLinePrice(key, parseFloat(e.target.value) || 0)
                          }
                          className="w-16 sm:w-24 h-7 text-right text-xs"
                        />
                      </td>
                      <td className="text-right">
                        {fmtCurrency(
                          effectivePrice * it.qty,
                          settings.currency
                        )}
                      </td>
                    </tr>
                  );
                }),
              ])}
              {settings.travelSurcharge > 0 && (
                <tr className="border-b">
                  <td className="py-1"></td>
                  <td className="font-mono">—</td>
                  <td>{t('invoice.travelSurcharge')}</td>
                  <td className="text-right">1</td>
                  <td className="text-muted-foreground">{t('invoice.unit')}</td>
                  <td className="text-right">
                    {fmtCurrency(settings.travelSurcharge, settings.currency)}
                  </td>
                  <td className="text-right">
                    {fmtCurrency(settings.travelSurcharge, settings.currency)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollTable>

        <div className="flex justify-end">
          <div className="w-full sm:w-72 text-sm space-y-1">
            <div className="no-print flex items-center justify-between gap-2">
              <Label htmlFor={vatInputId} className="text-muted-foreground">
                {t('invoice.vatOverride')}
              </Label>
              <Input
                id={vatInputId}
                data-testid="invoice-vat-input"
                type="number"
                min="0"
                step="0.1"
                value={effectiveVat}
                onChange={e => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setVatOverride(null);
                    return;
                  }
                  const parsed = parseFloat(raw);
                  setVatOverride(Number.isFinite(parsed) ? parsed : null);
                }}
                className="w-20 h-7 text-right text-xs"
              />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {t('invoice.totalExcl')}
              </span>
              <span>{fmtCurrency(subtotal, settings.currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {country.taxLabel} {effectiveVat}%
              </span>
              <span>{fmtCurrency(vat, settings.currency)}</span>
            </div>
            {Math.abs(roundingDiff) >= 0.005 && (
              <div
                data-testid="invoice-rounding-row"
                className="flex justify-between"
              >
                <span className="text-muted-foreground">
                  {t('invoice.rounding')}
                </span>
                <span data-testid="invoice-rounding-value">
                  {fmtCurrency(roundingDiff, settings.currency)}
                </span>
              </div>
            )}
            <div
              data-testid="invoice-total-row"
              className="flex justify-between border-t pt-1 font-bold text-base"
            >
              <span>{t('invoice.totalIncl')}</span>
              <span data-testid="invoice-total-value">
                {fmtCurrency(total, settings.currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-8 text-xs text-muted-foreground space-y-1">
          <div>
            {t('invoice.paymentTerms')}: {settings.paymentTerms}{' '}
            {t('invoice.daysNet')}
          </div>
          {settings.iban && <div>IBAN: {settings.iban}</div>}
          <div className="pt-4">{t('invoice.thanks')}</div>
        </div>
      </Card>
    </div>
  );
}
